import { Fragment, useState } from 'react'
import type { EyemedBenefits, EyemedEligibilityRow, EyemedTab, EyemedTable, EyemedTabsBenefits } from '../types'
import { isEyemedTabsBenefits } from '../types'
import {
  ELIGIBILITY_COULD_NOT_READ, eligibilityBlock, eligibilityCaptions, isBalancesTable,
  panelRowCount, panelSections, panelTables, tabSentence,
} from '../utils/eyemedTabs'
import { eyemedHeader, NOT_SHOWN } from '../utils/eyemedHeader'
import { DASH } from './SlideoutHeader'
import Accordion from './Accordion'
import SmsRecoverySection from './SmsRecoverySection'
import VisionSlideoutFrame, { type VisionCheckSummary } from './VisionSlideoutFrame'
import CheckIdentityEditor from './CheckIdentityEditor'
import { UNSAVED_WARNING } from '../utils/unsavedWork'
import { useUnsavedGuard } from '../utils/useUnsavedGuard'
import { canCorrect } from '../utils/checkCorrection'
import type { EligibilityCheckDoc } from '../types'

interface EyemedSlideoutProps {
  /** The stored result in either shape: the benefits page tab by tab (the
   *  in-house lane, `EyemedTabsBenefits`) or the category map (the retired
   *  bot, and every check stored before the lane read the page tab by tab)
   *  — told apart by `isEyemedTabsBenefits`, each with its own body below. */
  benefits: EyemedBenefits | EyemedTabsBenefits
  summary: VisionCheckSummary
  onClose: () => void
  /** True when the displayed stored check is in the SMS recovery loop. */
  inRecoveryLoop?: boolean
  /** True when the stored check is SMS-eligible (recoveryAction
   *  patient_correction) — shows the SMS recovery section even before the
   *  outreach sweep claims the check (link disabled until it does). */
  smsRecoveryEligible?: boolean
  /** Jump to this check's Recovery Loop case (the SMS conversation). */
  onViewRecoveryConversation?: () => void
  /** #314 — in-place correction. The vision panels had no correction path at
   *  all, so a wrong member id or payer on an RPA check meant starting over
   *  somewhere else. Absent → the editor is not offered. */
  orgId?: string
  check?: EligibilityCheckDoc | null
  onCorrected?: () => void
  /** Refresh hook fired the moment a correction runs, before any clinic-mapping
   *  offer is answered — so closing the panel cannot leave a stale view. */
  onCorrectionFired?: () => void
  /** False during a read-only (view-mode) staff session. Without it the fields
   *  and Save are live and the handler's `requireWrite` refuses only AFTER
   *  someone has typed into PHI fields. */
  canWrite?: boolean
  /** Unsaved-edit signal for the DASHBOARD's browser-Back sentinel (#274). The
   *  panel's own guard covers Escape / the overlay / the X, but Back never
   *  reaches a panel handler — without this channel `detailDirty` had exactly
   *  one producer (the failure slideout) and a typed correction here was
   *  discarded by Back with no warning at all. */
  onDirtyChange?: (dirty: boolean) => void
  /** The parent refused a close on this panel's behalf — browser Back, which
   *  the panel can't see. Renders the same inline bar so the refused press has
   *  a visible reason instead of being a dead press (#274, #231). */
  warnedByParent?: boolean
}

// ── The legacy category map: today's rendering, kept as it is ────────────
// The retired bot's shape, and every check stored before the in-house lane
// read the benefits page tab by tab. Firestore sorted its keys on write, so
// the portal's order is already gone here; the panel sorts for a stable
// list and filters the heading names it knows. No Medical section: the
// check never read one.

/** Section header labels that are just category titles, not real benefit lines. */
const HEADER_VALUES = new Set([
  'Exam Services',
  'Lenses',
  'Lens Options',
  'Contact Lenses',
  'Contact Lens Fit and Follow-Up',
  'Packages',
])

function isHeaderRow(key: string, value: string): boolean {
  return key === value || HEADER_VALUES.has(value)
}

/** Fixed display order — categories not listed here sort alphabetically after these. */
const CATEGORY_ORDER: string[] = ['Routine', 'Lasik', 'Additional Purchase']

/** Benefit labels pushed to the bottom within their category (catch-all entries). */
const SORT_LAST_LABELS = new Set(['All Other Lens Options'])

function sortCategories(entries: [string, Record<string, string>][]): [string, Record<string, string>][] {
  return entries.slice().sort(([a], [b]) => {
    const ia = CATEGORY_ORDER.indexOf(a)
    const ib = CATEGORY_ORDER.indexOf(b)
    if (ia !== -1 && ib !== -1) return ia - ib
    if (ia !== -1) return -1
    if (ib !== -1) return 1
    return a.localeCompare(b)
  })
}

function LegacyBody({ benefits }: { benefits: EyemedBenefits }) {
  const sorted = sortCategories(Object.entries(benefits))
  const [expandedCategory, setExpandedCategory] = useState<string | null>(sorted[0]?.[0] ?? null)

  return (
    <div className="accordion-stack">
      {sorted.map(([category, lines]) => {
        const benefitLines = Object.entries(lines)
          .filter(([k, v]) => !isHeaderRow(k, v))
          .sort(([a], [b]) => {
            const aLast = SORT_LAST_LABELS.has(a)
            const bLast = SORT_LAST_LABELS.has(b)
            if (aLast !== bLast) return aLast ? 1 : -1
            return a.localeCompare(b)
          })

        if (benefitLines.length === 0) return null

        const isOpen = expandedCategory === category

        return (
          <Accordion
            key={category}
            title={category}
            aside={<span className="accordion__count">{benefitLines.length}</span>}
            open={isOpen}
            onToggle={() => setExpandedCategory(isOpen ? null : category)}
            keepInView
            flush
          >
            <div className="eyemed-benefits-table">
              {benefitLines.map(([label, value]) => (
                <div className="eyemed-benefits-row" key={label}>
                  <span className="eyemed-benefits-row__label">{label}</span>
                  <span className="eyemed-benefits-row__value">{value}</span>
                </div>
              ))}
            </div>
          </Accordion>
        )
      })}
    </div>
  )
}

// ── The tabs shape: the benefits page as the portal printed it ───────────
// First the Eligibility block: the portal's per-service table, its sentence
// when the table was read empty or could not be read, nothing for a check
// that carries none (`eligibilityBlock`). Then one section per tab the portal
// listed, in the portal's order, plus the Medical section when none was
// listed (`panelSections`). A tab with rows is an accordion showing — at the
// top, when the tab carries one, the balances table whole, every column kept
// — then each benefit table under its header cells, each group heading over
// its rows, and every row's label and value as printed — no sort, no verdict
// (`panelTables`). A tab read with no rows or that could not be read shows
// its sentence in place of a body, open, so the answer is read without a
// click.

/** A balances table's rows: every cell as an equal column, header row
 *  included — never the benefit rows' label/value split. */
function BalancesRows({ table }: { table: EyemedTable }) {
  return (
    <>
      {table.header.length > 0 && (
        <div className="eyemed-benefits-row eyemed-benefits-row--header eyemed-benefits-row--balances">
          {table.header.map((cell, cellIndex) => (
            <span key={cellIndex} className="eyemed-balances-cell">{cell}</span>
          ))}
        </div>
      )}
      {table.groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {group.heading !== null && (
            <div className="eyemed-benefits-row eyemed-benefits-row--heading">
              <span className="eyemed-benefits-row__label">{group.heading}</span>
            </div>
          )}
          {group.rows.map((row, rowIndex) => (
            <div className="eyemed-benefits-row eyemed-benefits-row--balances" key={rowIndex}>
              {row.cells.map((cell, cellIndex) => (
                <span key={cellIndex} className="eyemed-balances-cell">{cell}</span>
              ))}
            </div>
          ))}
        </Fragment>
      ))}
    </>
  )
}

/** A benefit table's rows: the header cells, each group heading over its
 *  rows, every row's label and value as printed. */
function BenefitRows({ table }: { table: EyemedTable }) {
  return (
    <>
      {table.header.length > 0 && (
        <div className="eyemed-benefits-row eyemed-benefits-row--header">
          {table.header.map((cell, cellIndex) => (
            <span
              key={cellIndex}
              className={cellIndex === 0 ? 'eyemed-benefits-row__label' : 'eyemed-benefits-row__value'}
            >
              {cell}
            </span>
          ))}
        </div>
      )}
      {table.groups.map((group, groupIndex) => (
        <Fragment key={groupIndex}>
          {group.heading !== null && (
            <div className="eyemed-benefits-row eyemed-benefits-row--heading">
              <span className="eyemed-benefits-row__label">{group.heading}</span>
            </div>
          )}
          {group.rows.map((row, rowIndex) => (
            <div className="eyemed-benefits-row" key={rowIndex}>
              {row.cells.map((cell, cellIndex) => (
                <span
                  key={cellIndex}
                  className={cellIndex === 0 ? 'eyemed-benefits-row__label' : 'eyemed-benefits-row__value'}
                >
                  {cell}
                </span>
              ))}
            </div>
          ))}
        </Fragment>
      ))}
    </>
  )
}

function TabTables({ tab }: { tab: EyemedTab }) {
  return (
    <div className="eyemed-benefits-table">
      {panelTables(tab).map((table, tableIndex) => (
        <Fragment key={tableIndex}>
          {isBalancesTable(table) ? <BalancesRows table={table} /> : <BenefitRows table={table} />}
        </Fragment>
      ))}
    </div>
  )
}

/** The portal's per-service table: Service, Member is Eligible? and Member
 *  Eligible As Of as columns, captioned from the stored header, and the
 *  Service Frequency as a line under each row — every cell as printed, a
 *  blank one blank, never a verdict, a count or a total. */
function EligibilityTable({ header, rows }: { header: string[]; rows: EyemedEligibilityRow[] }) {
  const captions = eligibilityCaptions(header)
  return (
    <div className="eyemed-eligibility__table">
      <div className="eyemed-eligibility__row eyemed-eligibility__row--header">
        <span className="eyemed-eligibility__service">{captions.service}</span>
        <span className="eyemed-eligibility__eligible">{captions.eligible}</span>
        <span className="eyemed-eligibility__as-of">{captions.eligibleAsOf}</span>
      </div>
      {rows.map((row, rowIndex) => (
        <div className="eyemed-eligibility__row" key={rowIndex}>
          <span className="eyemed-eligibility__service">{row.service}</span>
          <span className="eyemed-eligibility__eligible">{row.eligible}</span>
          <span className="eyemed-eligibility__as-of">{row.eligibleAsOf}</span>
          {row.frequency !== '' && (
            <span className="eyemed-eligibility__frequency">{captions.frequency}: {row.frequency}</span>
          )}
        </div>
      ))}
    </div>
  )
}

function EligibilityBlock({ benefits }: { benefits: EyemedTabsBenefits }) {
  const view = eligibilityBlock(benefits)
  if (view === 'none') return null
  return (
    <section className="eyemed-eligibility" aria-label="Eligibility">
      <div className="eyemed-eligibility__title">Eligibility</div>
      {view === 'empty' && <p className="eyemed-eligibility__note">{NOT_SHOWN}</p>}
      {view === 'failed' && (
        <p className="eyemed-eligibility__note eyemed-eligibility__note--failed">{ELIGIBILITY_COULD_NOT_READ}</p>
      )}
      {/* Every tab of the answer as its own table, in the portal's order:
          the shown one ("Routine", the covered services — the one table the
          Yes/No verdict reads) first, then the others ("Additional Purchase",
          the materials as discount purchases), each under the tab's name;
          an answer that was one table with no tabs carries no name. */}
      {typeof view === 'object' && view.tabs.map((tab, tabIndex) => (
        <Fragment key={tabIndex}>
          {tab.name && <div className="eyemed-eligibility__tab">{tab.name}</div>}
          <EligibilityTable header={tab.header} rows={tab.rows} />
        </Fragment>
      ))}
    </section>
  )
}

function TabNote({ tab }: { tab: EyemedTab }) {
  if (tab.state === 'rows') return null
  return (
    <div className={`eyemed-tab-note${tab.state === 'failed' ? ' eyemed-tab-note--failed' : ''}`}>
      <span className="eyemed-tab-note__title">{tab.name}</span>
      <span className="eyemed-tab-note__text">{tabSentence(tab.name, tab.state)}</span>
    </div>
  )
}

function TabsBody({ benefits }: { benefits: EyemedTabsBenefits }) {
  const sections = panelSections(benefits)
  // The first section with rows opens, as the first category does today.
  const [expanded, setExpanded] = useState<number>(sections.findIndex(tab => tab.state === 'rows'))

  return (
    <>
      <EligibilityBlock benefits={benefits} />
      <div className="accordion-stack">
        {sections.map((tab, index) => {
          if (tab.state !== 'rows') return <TabNote key={index} tab={tab} />
          const isOpen = expanded === index
          return (
            <Accordion
              key={index}
              title={tab.name}
              aside={<span className="accordion__count">{panelRowCount(tab)}</span>}
              open={isOpen}
              onToggle={() => setExpanded(isOpen ? -1 : index)}
              keepInView
              flush
            >
              <TabTables tab={tab} />
            </Accordion>
          )
        })}
      </div>
    </>
  )
}

export default function EyemedSlideout({
  benefits,
  summary,
  onClose,
  inRecoveryLoop,
  smsRecoveryEligible,
  onViewRecoveryConversation,
  orgId,
  check,
  onCorrected,
  onCorrectionFired,
  canWrite = true,
  onDirtyChange,
  warnedByParent = false,
}: EyemedSlideoutProps) {
  // #274 — one component, four surfaces, one behaviour: closing this panel
  // mid-correction warns before it discards.
  const { guardedClose, setDirty, showWarning, dirty } = useUnsavedGuard(onClose)

  // The header EyeMed's own result composes (utils/eyemedHeader.ts): the
  // member EyeMed returned, with the record's ID under it only when it
  // differs; a check without the blocks keeps the frame's header as it is.
  const header = isEyemedTabsBenefits(benefits)
    ? eyemedHeader(benefits, summary.memberId, check?.memberIdSource)
    : null
  const payerDetails = header
    ? {
      plan: header.plan ?? undefined,
      group: header.group ?? undefined,
      memberId: (
        <>
          <div>{header.memberId || DASH}</div>
          {header.memberIdNote && <div className="med-meta">{header.memberIdNote}</div>}
        </>
      ),
    }
    : undefined

  return (
    <VisionSlideoutFrame
      subtitle="EyeMed eligibility overview"
      ariaLabel="EyeMed Benefits"
      summary={summary}
      payerDetails={payerDetails}
      onClose={guardedClose}
    >
      {isEyemedTabsBenefits(benefits)
        ? <TabsBody benefits={benefits} />
        : <LegacyBody benefits={benefits} />}

      <SmsRecoverySection
        inRecoveryLoop={inRecoveryLoop}
        smsRecoveryEligible={smsRecoveryEligible}
        onView={onViewRecoveryConversation}
      />
      {orgId && check && onCorrected && canCorrect(check) && (
        <div className="failure-slideout__edit">
          <CheckIdentityEditor
            canWrite={canWrite}
            onFired={onCorrectionFired}
            orgId={orgId}
            check={check}
            onDirtyChange={d => { setDirty(d); onDirtyChange?.(d) }}
            onCorrected={onCorrected}
          />
        </div>
      )}
          {(showWarning || (warnedByParent && dirty)) && (
            <div className="slideout-unsaved" role="status">
              <span>{UNSAVED_WARNING}</span>
              <button className="slideout-unsaved__discard" onClick={onClose}>
                Discard
              </button>
            </div>
          )}

    </VisionSlideoutFrame>
  )
}
